import {assertUnreachable} from '@wandb/weave/common/util/types';

import {
  EventTriggeringConditionType,
  TriggerScopeType,
} from '../../../generated/graphql';
import {capitalizeFirstLetter} from '../../../util/string';
import {
  checkRegistryProject,
  isOrgRegistryProjectName,
} from '../../Registries/common/utils';
import {DEFAULT_LAUNCH_PROJECT_NAME} from '../constants';
import {getRunMetricRunFilterString} from './CreateTriggerDrawer/RunFilterShared';
import {
  getRunMetricChangeString,
  type RunMetricChangeConfig,
} from './CreateTriggerDrawer/RunMetricChangeSection';
import {
  getAggOpStr,
  type RunMetricThresholdConfig,
} from './CreateTriggerDrawer/RunMetricThresholdSection';
import {
  getRunMetricZscoreString,
  type RunMetricZscoreConfig,
} from './CreateTriggerDrawer/RunMetricZscoreSection';
import {
  getRunStateString,
  type RunStateConfig,
} from './CreateTriggerDrawer/RunStateSection';
import {
  getWeaveMetricThresholdString,
  type WeaveMetricThresholdConfig,
} from './CreateTriggerDrawer/WeaveMetricAlertSection';
import {getValueFromArtifactFilterString} from './filterUtils';
import {type NotificationConfig} from './notificationUtils';
import {isKnownEventType} from './TriggerEventTypes';
import {isKnownScopeType} from './TriggerScopeTypes';
import {type TriggerActionType} from './TriggersTable/utilTypes';

type TriggerDescriptionInput = {
  eventType?: EventTriggeringConditionType;
  triggerScopeType?: TriggerScopeType;
  entityName?: string;
  projectName?: string;
  artifactCollectionName?: string;
  filters?: string;
  runMetricConfig?: RunMetricThresholdConfig;
  runMetricChangeConfig?: RunMetricChangeConfig;
  runMetricZscoreConfig?: RunMetricZscoreConfig;
  runStateConfig?: RunStateConfig;
  weaveMetricThresholdConfig?: WeaveMetricThresholdConfig;
  notificationConfig?: NotificationConfig;
  ariaPrompt?: string;
};

// Keep the summary action body on one line and short enough for the card.
const ARIA_PROMPT_PREVIEW_MAX_LENGTH = 80;

const truncateARIAPromptPreview = (prompt: string): string => {
  const normalized = prompt.trim().replace(/\s+/g, ' ');
  if (normalized.length <= ARIA_PROMPT_PREVIEW_MAX_LENGTH) {
    return normalized;
  }
  return `${normalized.slice(0, ARIA_PROMPT_PREVIEW_MAX_LENGTH - 3)}…`;
};

type DescriptionPart = {
  title: string;
  body: string;
};

export type DescriptionType = {
  actionType?: TriggerActionType;
  event: DescriptionPart;
  action: DescriptionPart;
  stringVal: string;
};

export const isLegacyModelRegistryProjectName = (
  projectName: string | undefined
) => projectName === DEFAULT_LAUNCH_PROJECT_NAME;

const isUserVisibleProjectName = (projectName: string | undefined) =>
  projectName != null &&
  projectName !== '' &&
  !isOrgRegistryProjectName(projectName) &&
  !isLegacyModelRegistryProjectName(projectName);

const collectionScopeDescription = (input: TriggerDescriptionInput) => {
  const inputRegistryInfo = checkRegistryProject(input.projectName);

  switch (input.triggerScopeType) {
    case TriggerScopeType.ArtifactCollection:
      return inputRegistryInfo.isRegistryProject
        ? `the collection "${input.artifactCollectionName}".`
        : `the artifact "${input.artifactCollectionName}".`;
    case TriggerScopeType.Project:
      if (inputRegistryInfo.isRegistryProject) {
        return `any collection in the ${inputRegistryInfo.registryName} registry.`;
      }
      if (isLegacyModelRegistryProjectName(input.projectName)) {
        return `any artifact in the entity "${input.entityName}'s" model registry.`;
      }
      if (isUserVisibleProjectName(input.projectName)) {
        return `any artifact version in the project "${input.projectName}".`;
      }
      return `any collection in the registry.`;
    case TriggerScopeType.Entity:
      // Reached only by artifact/link/tag events (run events build their own
      // description). `entityName` holds the org or team entity name, and an
      // entity-scoped automation hears the event in any collection under it.
      // Guard the interpolation so a missing name never renders as "undefined".
      return input.entityName
        ? `any collection within "${input.entityName}".`
        : `any collection within this scope.`;
    case undefined:
      return `(event not yet configured)`;
    default:
      if (!isKnownScopeType(input.triggerScopeType)) {
        return `(unsupported scope type)`;
      }
      assertUnreachable(input.triggerScopeType);
  }
};

// Derive default trigger 'description' based on event type and user inputs
export const getDefaultTriggerDescription = (
  input: TriggerDescriptionInput,
  actionType?: TriggerActionType,
  jobName?: string,
  jobAlias?: string,
  webhookName?: string,
  slackChannelName?: string
) => {
  // Design needs a fancy way to display the description
  // Return a dictionary with the string value and the UI components
  let descriptionDict: DescriptionType = {
    actionType,
    event: {
      title: 'In the event that',
      body: '',
    },
    action: {
      title: '',
      body: '',
    },
    stringVal: '',
  };
  let stringStart = '';
  switch (actionType) {
    case undefined:
      return descriptionDict;
    case 'QueueJobTriggeredAction':
      if (jobName && jobAlias) {
        stringStart = `Launch the job "${jobName}:${jobAlias}" whenever `;
        descriptionDict = {
          ...descriptionDict,
          action: {title: 'Launch the job', body: `${jobName}:${jobAlias}`},
        };
      }
      break;
    case 'GenericWebhookTriggeredAction':
      if (webhookName) {
        stringStart = `Trigger the webhook "${webhookName}" whenever `;
        descriptionDict = {
          ...descriptionDict,
          action: {title: 'Trigger the webhook', body: webhookName},
        };
      }
      break;
    case 'NotificationTriggeredAction': {
      stringStart = `Send a Slack notification whenever `; // Future: add channel name
      descriptionDict = {
        ...descriptionDict,
        action: {
          title: 'Send Slack notification',
          body: slackChannelName ? `To ${slackChannelName}` : '',
        },
      };
      break;
    }
    case 'PushNotificationTriggeredAction': {
      stringStart = `Send a push notification whenever `;
      descriptionDict = {
        ...descriptionDict,
        action: {title: 'Send push notification', body: 'to mobile devices'},
      };
      break;
    }
    case 'ARIATriggeredAction': {
      stringStart = `Trigger ARIA whenever `;
      descriptionDict = {
        ...descriptionDict,
        action: {
          title: 'Trigger ARIA',
          body: input.ariaPrompt
            ? truncateARIAPromptPreview(input.ariaPrompt)
            : '',
        },
      };
      break;
    }
    case 'NoOpTriggeredAction':
      descriptionDict = {
        ...descriptionDict,
        action: {title: 'Do nothing', body: '—'},
      };
      break;
    default:
      assertUnreachable(actionType);
  }

  let stringEnd = '';
  switch (input.eventType) {
    case undefined:
      // this function is run before configuration is done, so it just returns defaults when
      // the data is not yet available
      break;
    case EventTriggeringConditionType.LinkModel: {
      const collectionDesc = collectionScopeDescription(input);
      stringEnd = `a new version is added to ${collectionDesc}`;
      break;
    }
    case EventTriggeringConditionType.UnlinkArtifact: {
      const collectionDesc = collectionScopeDescription(input);
      stringEnd = `a version is unlinked from ${collectionDesc}`;
      break;
    }
    case EventTriggeringConditionType.CreateArtifact: {
      stringEnd = `a new version of "${input.artifactCollectionName}" is created.`;
      break;
    }
    case EventTriggeringConditionType.AddArtifactAlias:
    case EventTriggeringConditionType.UpdateArtifactAlias: {
      // In the case of the AddAlias or UpdateAlias trigger event, the first filter gives us the alias name
      const alias = getValueFromArtifactFilterString(input.filters, 'alias');
      const collectionDesc = collectionScopeDescription(input);
      stringEnd =
        input.eventType === EventTriggeringConditionType.AddArtifactAlias
          ? `an alias matching "${alias}" is added to ${collectionDesc}`
          : `the alias is updated to ${alias} for ${collectionDesc}`;
      break;
    }
    case EventTriggeringConditionType.AddArtifactTag:
    case EventTriggeringConditionType.RemoveArtifactTag: {
      const tag = getValueFromArtifactFilterString(input.filters, 'tag');
      const verb =
        input.eventType === EventTriggeringConditionType.AddArtifactTag
          ? 'added to'
          : 'removed from';
      const collectionDesc = collectionScopeDescription(input);
      stringEnd = `a tag matching "${tag}" is ${verb} a version in ${collectionDesc}`;
      break;
    }
    case EventTriggeringConditionType.AddCollectionTag:
    case EventTriggeringConditionType.RemoveCollectionTag: {
      const tag = getValueFromArtifactFilterString(input.filters, 'tag');
      const verb =
        input.eventType === EventTriggeringConditionType.AddCollectionTag
          ? 'added to'
          : 'removed from';
      const collectionDesc = collectionScopeDescription(input);
      stringEnd = `a tag matching "${tag}" is ${verb} ${collectionDesc}`;
      break;
    }
    case EventTriggeringConditionType.RunMetric: {
      const {metricFilter, runFilter} = input.runMetricConfig ?? {};
      if (!metricFilter || !runFilter) {
        console.error('invalid runmetric state - missing filter parts', {
          metricFilter,
          runFilter,
        });
        return descriptionDict;
      }
      stringEnd = getRunMetricRunFilterString(runFilter);
      stringEnd += `${getAggOpStr(metricFilter.aggOp)} of "${
        metricFilter?.metricName
      }" ${metricFilter.cmpOp.label.toLowerCase()} ${
        metricFilter.threshold
      } over the last ${metricFilter.windowSize} values.`;
      break;
    }
    case EventTriggeringConditionType.RunMetricChange: {
      const {metricFilter, runFilter} = input.runMetricChangeConfig ?? {};
      if (!metricFilter || !runFilter) {
        console.error(
          'invalid runmetricchange state - missing runmetricchange config',
          {
            runMetricChangeConfig: input.runMetricChangeConfig,
          }
        );
        return descriptionDict;
      }
      stringEnd = getRunMetricChangeString(metricFilter, runFilter);
      break;
    }
    case EventTriggeringConditionType.RunState: {
      const {runStateFilter, runFilter} = input.runStateConfig ?? {};
      if (!runFilter || !runStateFilter) {
        console.error(
          'invalid runstate state - missing runfilter or runstatefilter',
          {
            runStateConfig: input.runStateConfig,
          }
        );
        return descriptionDict;
      }
      stringEnd = getRunStateString(runStateFilter, runFilter);
      break;
    }
    case EventTriggeringConditionType.RunMetricZscore: {
      const {metricFilter, runFilter} = input.runMetricZscoreConfig ?? {};
      if (!metricFilter || !runFilter) {
        console.error(
          'invalid runmetriczscore state - missing runmetriczscore config',
          {
            runMetricZscoreConfig: input.runMetricZscoreConfig,
          }
        );
        return descriptionDict;
      }
      stringEnd = getRunMetricZscoreString(metricFilter, runFilter);
      break;
    }
    case EventTriggeringConditionType.WeaveMetricThreshold: {
      const metricFilter = input.weaveMetricThresholdConfig?.metricFilter;
      if (!metricFilter) {
        console.error(
          'invalid weave metric threshold state - missing metricFilter',
          {
            weaveMetricThresholdConfig: input.weaveMetricThresholdConfig,
          }
        );
        return descriptionDict;
      }
      stringEnd = getWeaveMetricThresholdString(metricFilter);
      break;
    }
    default: {
      if (isKnownEventType(input.eventType)) {
        assertUnreachable(input.eventType);
      }
      // event type not yet supported by the frontend
      break;
    }
  }
  descriptionDict.event.body = capitalizeFirstLetter(stringEnd.slice(0, -1)); // Remove full stop from the event body.
  descriptionDict.stringVal = stringStart + stringEnd;
  return descriptionDict;
};
